import m from 'mithril'
import { css } from '@emotion/css'
import { colors, font, space, card, btnPrimary, btnSecondary } from '../styles/theme.js'
import { notificationsApi, projects as projectsApi } from '../lib/api.js'

const breadcrumb = css`
  font-size: ${font.size.sm};
  color: ${colors.textMuted};
  margin-bottom: ${space.lg};
  a { color: ${colors.textSecondary}; }
`

const titleRow = css`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${space.lg};
`

const pageTitle = css`
  font-size: ${font.size['2xl']};
  font-weight: ${font.weight.bold};
`

const notifList = css`
  ${card};
`

const notifRow = css`
  display: flex;
  gap: ${space.sm};
  padding: ${space.sm} ${space.md};
  border-bottom: 1px solid ${colors.border};
  cursor: pointer;
  transition: background 0.1s ease;
  &:last-child { border-bottom: none; }
  &:hover { background: ${colors.bg}; }
`

const notifRowUnread = css`
  background: ${colors.brandLight};
`

const notifDot = css`
  flex-shrink: 0;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-top: 6px;
`

const notifContent = css`
  flex: 1;
  min-width: 0;
`

const notifText = css`
  font-size: ${font.size.sm};
  color: ${colors.text};
  line-height: 1.5;
`

const notifPreview = css`
  font-size: ${font.size.xs};
  color: ${colors.textMuted};
  margin-top: 2px;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
`

const notifTime = css`
  flex-shrink: 0;
  font-size: ${font.size.xs};
  color: ${colors.textMuted};
  white-space: nowrap;
`

const emptyState = css`
  text-align: center;
  padding: ${space['2xl']};
  color: ${colors.textMuted};
`

const paginationBar = css`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: ${space.md};
  padding: ${space.md} 0;
`

function timeAgo(isoString) {
  if (!isoString) return ''
  const normalized = isoString.includes('T') ? isoString : isoString.replace(' ', 'T')
  const d = new Date(normalized.endsWith('Z') ? normalized : normalized + 'Z')
  const now = new Date()
  const diffMs = now - d
  const diffMin = Math.floor(diffMs / 60000)
  if (diffMin < 1) return 'just now'
  if (diffMin < 60) return `${diffMin}m ago`
  const diffHr = Math.floor(diffMin / 60)
  if (diffHr < 24) return `${diffHr}h ago`
  const diffDay = Math.floor(diffHr / 24)
  return `${diffDay}d ago`
}

function notifMessage(type, actorName) {
  switch (type) {
    case 'comment_reply':
      return `${actorName} replied to your comment`
    case 'session_comment':
      return `${actorName} commented in a session you participated in`
    default:
      return `${actorName} performed an action`
  }
}

export function NotificationsView() {
  let projectId = null
  let project = null
  let notifications = []
  let loading = true
  let memberMap = {}
  let page = 0
  const PAGE_SIZE = 50

  async function load() {
    loading = true
    try {
      const [notifs, proj] = await Promise.all([
        notificationsApi.list(projectId, { limit: PAGE_SIZE, offset: page * PAGE_SIZE }),
        projectsApi.get(projectId),
      ])
      notifications = notifs || []
      project = proj
      memberMap = {}
      if (proj.members) {
        for (const mem of proj.members) {
          memberMap[mem.id] = mem.name || mem.email
        }
      }
    } catch (err) {
      console.error('Failed to load notifications:', err)
      notifications = []
    } finally {
      loading = false
      m.redraw()
    }
  }

  async function markAllRead() {
    try {
      await notificationsApi.markAllRead(projectId)
      for (const n of notifications) n.read = 1
      m.redraw()
    } catch (err) {
      console.error('Failed to mark all read:', err)
    }
  }

  function goToSession(notif) {
    // Mark as read when clicked
    if (!notif.read) {
      notificationsApi.markRead(projectId, [notif.id]).catch(() => {})
      notif.read = 1
    }
    m.route.set(`/projects/${projectId}/sessions/${notif.session_id}`)
  }

  return {
    oninit(vnode) {
      projectId = vnode.attrs.projectId
      load()
    },

    view() {
      const hasUnread = notifications.some(n => !n.read)

      return m('div', [
        m('div', { class: breadcrumb }, [
          m(m.route.Link, { href: '/projects' }, 'Projects'),
          ' / ',
          m(m.route.Link, { href: `/projects/${projectId}` }, project?.name || 'Project'),
          ' / ',
          'Notifications',
        ]),

        m('div', { class: titleRow }, [
          m('h1', { class: pageTitle }, 'Notifications'),
          hasUnread
            ? m('button', { class: btnSecondary, onclick: markAllRead }, 'Mark all as read')
            : null,
        ]),

        loading
          ? m('div', { class: emptyState }, 'Loading...')
          : notifications.length === 0
            ? m('div', { class: emptyState }, 'No notifications yet. You\'ll be notified when someone replies to your comments or comments in sessions you\'ve participated in.')
            : m('div', { class: notifList },
                notifications.map(n => {
                  const actorName = memberMap[n.actor_id] || 'Someone'
                  return m('div', {
                    key: n.id,
                    class: `${notifRow} ${!n.read ? notifRowUnread : ''}`,
                    onclick: () => goToSession(n),
                  }, [
                    m('span', {
                      class: notifDot,
                      style: `background: ${n.read ? 'transparent' : colors.brand};`,
                    }),
                    m('div', { class: notifContent }, [
                      m('div', { class: notifText }, notifMessage(n.type, actorName)),
                      n.comment_preview
                        ? m('div', { class: notifPreview }, `"${n.comment_preview}"`)
                        : null,
                    ]),
                    m('span', { class: notifTime }, timeAgo(n.created_at)),
                  ])
                })
              ),

        notifications.length >= PAGE_SIZE
          ? m('div', { class: paginationBar }, [
              m('button', {
                class: btnSecondary,
                disabled: page === 0,
                onclick: () => { page--; load() },
              }, 'Previous'),
              m('span', { style: `font-size: ${font.size.sm}; color: ${colors.textMuted};` },
                `Page ${page + 1}`),
              m('button', {
                class: btnSecondary,
                onclick: () => { page++; load() },
              }, 'Next'),
            ])
          : null,
      ])
    },
  }
}
